/**
 * lib/rag/provenance.mjs — TICKET-1248 (RAG provenance plan R1).
 *
 * The ONE provenance contract for every RAG write (Chroma add/upsert, LightRAG insert):
 * who added it, when, what it is, and in what context.
 *
 *   buildProvenance({...}) -> { id, metadata }
 *     id       = '<sourceTable>:<sourceId>[:<chunk>]' (deterministic: re-index replaces, never duplicates)
 *     metadata = canonical flat keys (Chroma rejects null and objects): prov_user_id,
 *                prov_actor_kind, prov_source_table, prov_source_id, prov_kind, prov_title,
 *                prov_created_at, prov_indexed_at, project_id, deal_id, vessel_id,
 *                context_type, scope_level, sensitivity. Empty values are dropped.
 *   provenanceTag(meta, names?) -> 'Chris · 2026-09-12 · Proj X · voice_chip'
 *   readProvenance(meta) / withProvenanceTag / resolveProvenanceNames -> R5 readers (TICKET-1261)
 *   lightragDocKey(prov | metadata) -> the LightRAG file_path / rag_documents.doc_key (R4)
 *
 * Gate: scripts/check-rag-provenance.mjs fails a RAG writer that neither imports
 * buildProvenance nor is listed in config/rag-writers.json.
 */

export class ProvenanceError extends Error {
  constructor(message) {
    super(`provenance: ${message}`);
    this.name = 'ProvenanceError';
    this.code = 'PROVENANCE_INVALID';
  }
}

const ACTOR_KINDS = new Set(['user', 'system']);
const empty = (v) => v === undefined || v === null || v === '';

function iso(v, field) {
  if (empty(v)) return undefined;
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) throw new ProvenanceError(`${field} is not a valid date: ${String(v)}`);
  return d.toISOString();
}

export function buildProvenance({
  userId, actorKind = 'user', sourceTable, sourceId, kind, title, sourceCreatedAt,
  projectId, dealId, vesselId, contextType, scopeLevel, sensitivity, chunk,
} = {}) {
  if (empty(sourceTable)) throw new ProvenanceError('sourceTable is required');
  if (empty(sourceId)) throw new ProvenanceError('sourceId is required');
  if (!ACTOR_KINDS.has(actorKind)) throw new ProvenanceError(`actorKind must be 'user' or 'system', got ${String(actorKind)}`);
  if (empty(userId) && actorKind !== 'system') throw new ProvenanceError("userId is required unless actorKind is 'system'");

  const raw = {
    prov_user_id: empty(userId) ? 'system' : userId,
    prov_actor_kind: actorKind,
    prov_source_table: sourceTable,
    prov_source_id: sourceId,
    prov_kind: kind,
    prov_title: title,
    prov_created_at: iso(sourceCreatedAt, 'sourceCreatedAt'),
    prov_indexed_at: new Date().toISOString(),
    project_id: projectId,
    deal_id: dealId,
    vessel_id: vesselId,
    context_type: contextType,
    scope_level: scopeLevel,
    sensitivity,
  };
  const metadata = {};
  for (const [k, v] of Object.entries(raw)) if (!empty(v)) metadata[k] = String(v); // every key is text; ids stay join-safe
  const id = `${sourceTable}:${sourceId}${empty(chunk) ? '' : `:${chunk}`}`;
  return { id, metadata };
}

const first = (...vals) => { for (const v of vals) if (!empty(v)) return String(v); return null; };

/**
 * TICKET-1261 (R5): one normalised provenance object for a read hit. prov_* keys win;
 * vectors written before R3 fall back to their legacy keys; an already-normalised object
 * passes through unchanged. Never throws.
 */
export function readProvenance(meta) {
  const m = meta && typeof meta === 'object' ? meta : {};
  const by = first(m.prov_user_id, m.by, m.user_id, m.uploaded_by, m.added_by);
  return {
    by,
    actor_kind: first(m.prov_actor_kind, m.actor_kind) || (by === 'system' ? 'system' : by ? 'user' : null),
    at: first(m.prov_created_at, m.at, m.created_at, m.timestamp),
    kind: first(m.prov_kind, m.kind, m.doc_type, m.entry_kind, m.entity_type, m.source),
    title: first(m.prov_title, m.title, m.name),
    source_table: first(m.prov_source_table, m.source_table) || (empty(m.document_id) ? null : 'project_documents'),
    source_id: first(m.prov_source_id, m.source_id, m.document_id, m.voice_record_id),
    project_id: first(m.project_id),
    deal_id: first(m.deal_id),
    vessel_id: first(m.vessel_id),
    context_type: first(m.context_type),
  };
}

// Sensitivity tiers: which personas can see which tier (single source; agent/services/chroma.js
// retrieve() and lib/handlers/brain-search.js both filter with it). Missing tier = public.
export const SENSITIVITY_ACCESS = {
  public: ['ceo', 'pmo', 'shop', 'sales', 'client'],
  operational: ['ceo', 'pmo', 'sales'],
  confidential: ['ceo'],
};

/** Can this persona see this tier? 'admin' = 'ceo'; no persona -> everything but confidential. */
export function canSeeSensitivity(tier, persona) {
  const t = tier || 'public';
  if (!persona) return t !== 'confidential' && t in SENSITIVITY_ACCESS;
  return (SENSITIVITY_ACCESS[t] || []).includes(persona === 'admin' ? 'ceo' : persona);
}

export const TAG_MAX = 60;

/**
 * 'Chris · 2026-09-12 · Proj X · voice_chip' (<= TAG_MAX chars). Accepts raw metadata or a
 * readProvenance() object. names = { [userId|projectId|dealId|vesselId]: label }; an
 * unresolved user id is omitted (a raw uuid is noise in a prompt), 'system' stays.
 */
export function provenanceTag(meta, names = {}) {
  if (!meta) return '';
  const p = readProvenance(meta);
  const who = p.actor_kind === 'system' || p.by === 'system' ? 'system' : (p.by && names[p.by]) || '';
  const when = p.at ? p.at.slice(0, 10) : '';
  const where = (names[p.project_id] || names[p.deal_id] || names[p.vessel_id] || p.context_type || '').slice(0, 24);
  const tag = [who, when, where, p.kind].filter(Boolean).join(' · ');
  return tag.length > TAG_MAX ? `${tag.slice(0, TAG_MAX - 1)}…` : tag;
}

/** '<text> (<tag>)' — the one snippet format every prompt builder prints. */
export function withProvenanceTag(text, meta, names) {
  const tag = provenanceTag(meta, names);
  return tag ? `${text} (${tag})` : text;
}

/**
 * Display names for a request's hits: ONE batched user_profiles query (+ one projects
 * query when hits carry project ids). Returns { [id]: label }; any failure -> {} so the
 * caller's request never breaks over provenance.
 */
export async function resolveProvenanceNames(sb, metas = []) {
  const provs = (metas || []).map(readProvenance);
  // uuid-only: one legacy non-uuid id would make Postgres reject the whole .in() batch.
  const isUuid = (v) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(v));
  const userIds = [...new Set(provs.map((p) => p.by).filter(isUuid))];
  const projectIds = [...new Set(provs.map((p) => p.project_id).filter(isUuid))];
  const names = {};
  if (!sb || (!userIds.length && !projectIds.length)) return names;
  const load = async (table, cols, ids, label) => {
    if (!ids.length) return;
    try {
      const { data } = await sb.from(table).select(cols).in('id', ids);
      for (const r of data || []) if (r?.[label]) names[r.id] = r[label];
    } catch { /* spine-emit-ok: provenance names are decoration; the tag drops the name */ }
  };
  await Promise.all([
    load('user_profiles', 'id, display_name', userIds, 'display_name'),
    load('projects', 'id, project_name', projectIds, 'project_name'),
  ]);
  return names;
}

export function lightragDocKey(prov) {
  if (prov?.id) return prov.id;
  if (prov?.prov_source_table && prov?.prov_source_id) return `${prov.prov_source_table}:${prov.prov_source_id}`;
  throw new ProvenanceError('lightragDocKey needs a buildProvenance result or its metadata');
}
